import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=user
  const [error, setError] = useState("");

  useEffect(() => {
    if (typeof localStorage === "undefined") return;
    if (user && user.is_gov) localStorage.setItem("embi_gov", "1");
    else if (user === false) localStorage.removeItem("embi_gov");
  }, [user]);

  const checkAuth = useCallback(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    // If returning from Google OAuth (hash) or arriving via GI SSO (/enter), the dedicated
    // handler establishes the session first — skip the guest /me check so it can't clobber it.
    if (window.location.hash?.includes("session_id=") || window.location.pathname.startsWith("/enter")) {
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data && data.mfa_required) return { mfa: true, setup: !!data.mfa_setup_required };
      const me = await api.get("/auth/me");
      setUser(me.data);
      return { ok: true, user: me.data };
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return { error: true };
    }
  };

  // Emergent-managed Google Auth: redirect out; we come back to "/" with a #session_id.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    try {
      const next = new URLSearchParams(window.location.search).get("next");
      if (next) sessionStorage.setItem("embi_post_login_next", next);
    } catch (_) {}
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Exchange the one-time session_id (from the URL fragment) for a session.
  const processGoogleSession = async (sessionId) => {
    const { data } = await api.post("/auth/session", {}, { headers: { "X-Session-ID": sessionId } });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    setUser(false);
  };

  const unlockPro = async () => {
    await api.post("/billing/unlock");
    const r = await api.get("/auth/me");
    setUser(r.data);
  };

  const refreshUser = useCallback(async () => {
    try { const r = await api.get("/auth/me"); setUser(r.data); return r.data; } catch { return null; }
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, loginWithGoogle, processGoogleSession, logout, unlockPro, refreshUser, error, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export const isPro = (user) => !!(user && (user.is_pro || user.effective_pro));
